import { clsx, type ClassValue } from "clsx";
import { formatDuration } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { Base64 } from "js-base64";
import _ from "lodash";
import * as React from "react";
import { Params } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { ImpactStatus, LikelihoodBand } from "~/models";

export class Helpers {
  static secondsToDuration(seconds: number) {
    if (seconds === 0) return "0s";

    const duration = formatDuration(
      {
        hours: Math.floor(seconds / 3600),
        minutes: Math.floor((seconds % 3600) / 60),
        seconds: Math.floor(seconds % 60),
      },
      { delimiter: " " },
    );
    return duration
      .replace(/ hours?/, "h")
      .replace(/ minutes?/, "m")
      .replace(/ seconds?/, "s");
  }

  static policyPath({
    scopeId,
    policyId,
  }: {
    scopeId: string;
    policyId: string;
  }) {
    return `/policies/${scopeId}/${Base64.encode(policyId)}`;
  }

  static journeyPath({
    scopeId,
    policyId,
    journeyId,
  }: {
    scopeId: string;
    policyId: string;
    journeyId: string;
  }) {
    return `/policies/${scopeId}/${Base64.encode(policyId)}/journeys/${Base64.encode(journeyId)}`;
  }

  static displayValue(value: any) {
    return value === "" ||
      value === null ||
      value === undefined ||
      value === false
      ? "---"
      : value;
  }

  static rounded(value: number, fractionDigits: number = 1) {
    if (value % 1 !== 0) return Number(value.toFixed(fractionDigits));
    return value;
  }

  static bearingToDirection(bearing: number): string {
    if (bearing < 0 || bearing > 360) {
      return "---";
    }
    if (bearing >= 337.5 || bearing < 22.5) {
      return "North";
    } else if (bearing >= 22.5 && bearing < 67.5) {
      return "North East";
    } else if (bearing >= 67.5 && bearing < 112.5) {
      return "East";
    } else if (bearing >= 112.5 && bearing < 157.5) {
      return "South East";
    } else if (bearing >= 157.5 && bearing < 202.5) {
      return "South";
    } else if (bearing >= 202.5 && bearing < 247.5) {
      return "South West";
    } else if (bearing >= 247.5 && bearing < 292.5) {
      return "West";
    } else if (bearing >= 292.5 && bearing < 337.5) {
      return "North West";
    }
    return "---";
  }

  static quadrant(bearing: number) {
    const roundedBearing = Math.round(bearing);

    if (roundedBearing <= 90) return `N ${roundedBearing} E`;
    else if (roundedBearing <= 180) return `S ${180 - roundedBearing} E`;
    else if (roundedBearing <= 270) return `S ${roundedBearing - 180} W`;
    else return `N ${360 - roundedBearing} W`;
  }
}

export const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs));
};

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
export const composeEventHandlers = (fns: Function[]) => {
  return (event: React.SyntheticEvent, ...args: []) => {
    for (const fn of fns) {
      if (event.defaultPrevented) break;
      if (typeof fn === "function") fn(event, ...args);
    }
  };
};

export const now = () => {
  return Math.floor(Date.now() / 1000);
};

export const filterParams = (params: Params, allowedKeys: string[]) => {
  return allowedKeys.reduce(
    (acc, key) => ({
      ...acc,
      ...(params[key] && {
        [key]: params[key],
      }),
    }),
    {},
  );
};

export const generateId = () => {
  return Date.now().toString();
};

export const isReactElement = (element: React.ReactNode) => {
  return React.isValidElement(element);
};

export const isElementWithChildren = (element: React.ReactNode) => {
  return typeof (element as React.ReactElement).props.children !== "undefined";
};

export const wait = (timer: number = 1000) =>
  new Promise((resolve) => setTimeout(resolve, timer));

export const csrfToken = () => {
  const meta = document.querySelector("meta[name=csrf-token]");
  const token = meta && meta.getAttribute("content");
  return token ?? false;
};

export const bandTagVariant = (band: LikelihoodBand) => {
  if (band === "UNLIKELY") return "amber";
  else if (band === "LIKELY") return "amber";
  else if (band === "VERY_LIKELY") return "red";
  else return "default";
};

export const statusTagVariant = (status: ImpactStatus) => {
  if (status === "ARCHIVED") return "default";
  else if (status === "COMPLETE") return "green";
  else if (status === "IN_PROGRESS") return "amber";
  else if (status === "NEW") return "red";
  else return "default";
};

/**
 * Checks we have data for arrays, strings, numbers, etc
 */
export const hasData = <T>(data: unknown): data is T[] | boolean => {
  return Array.isArray(data)
    ? data.length > 0
    : data !== null && data !== undefined && data !== "";
};

/**
 * Helper to return first item from an array
 */
export const firstItem = <T>(data: unknown): T | null => {
  return Array.isArray(data) ? (data[0] as T) : null;
};

/**
 * Helper to return last item from an array
 */
export const lastItem = <T>(data: unknown): T | null => {
  return Array.isArray(data) ? (data[data.length - 1] as T) : null;
};

export const getLocale = (): string =>
  document.head.querySelector("html")?.getAttribute("lang") || "en-us";

export const formatSpeed = (
  speed: number,
  locale: string = getLocale(),
): string => {
  if (!hasData(speed)) return "---";

  return new Intl.NumberFormat(locale, {
    style: "unit",
    unit: "mile-per-hour",
  }).format(speed);
};

export const capitalize = (string: string) => {
  return _.words(string, /[\w']+/g)
    .map((w) => _.capitalize(w))
    .join(" ");
};

export const isTest =
  typeof process !== "undefined" && process.env?.VITEST === "true";

export const formatDateTime = (
  date: string | number | Date,
  format: string = "EEEE do LLLL yyyy",
  timeZone: string = isTest
    ? "UTC" // tests should run as UTC
    : Intl.DateTimeFormat().resolvedOptions().timeZone,
) => formatInTimeZone(new Date(date), timeZone, format);
